import React, { useState } from 'react';
import { ArrowRight, ChevronRight, Star, Quote, Truck, RefreshCw, Shield, Clock, Play, ChevronLeft, Instagram, Heart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Template 6: SYMMETRY
 * Style: Editorial, Magazine-inspired, Clean Grid
 * Best for: Contemporary Indian fashion, Editorial brands, Modern ethnic fusion
 * Light theme with warm amber accents
 */
const Template6 = () => {
    const navigate = useNavigate();
    const [activeEditorial, setActiveEditorial] = useState(0);

    // Theme configuration - Light Editorial
    const theme = {
        colors: {
            primary: '#1C1917',
            secondary: '#78716C',
            accent: '#D97706',
            accentLight: '#FEF3C7',
            background: '#FFFBEB',
            surface: '#FFFFFF',
            warm: '#FEF7ED',
            text: {
                primary: '#1C1917',
                secondary: '#57534E',
                muted: '#A8A29E'
            }
        }
    };

    // Editorial categories - Indian Fashion
    const categories = [
        { name: 'Kurtas & Kurtis', count: '180+ Styles', image: 'https://placehold.co/600x800/FEF7ED/1C1917?text=Kurtas' },
        { name: 'Sarees', count: '250+ Styles', image: 'https://placehold.co/600x800/FEF3C7/1C1917?text=Sarees' },
        { name: 'Lehengas', count: '120+ Styles', image: 'https://placehold.co/600x800/FDE68A/1C1917?text=Lehengas' },
        { name: 'Indo-Western', count: '95+ Styles', image: 'https://placehold.co/600x800/FEF7ED/1C1917?text=Indo-Western' }
    ];

    // Featured products
    const products = [
        { id: 1, name: 'Chanderi Silk Kurta Set', price: '₹4,299', originalPrice: '₹5,999', badge: 'Bestseller', image: 'https://placehold.co/400x500/FFFBEB/1C1917?text=Kurta+1' },
        { id: 2, name: 'Banarasi Zari Saree', price: '₹8,499', originalPrice: '₹11,999', badge: 'Handwoven', image: 'https://placehold.co/400x500/FEF7ED/1C1917?text=Saree+1' },
        { id: 3, name: 'Embroidered Anarkali', price: '₹6,799', originalPrice: '₹8,999', badge: 'New', image: 'https://placehold.co/400x500/FEF3C7/1C1917?text=Anarkali' },
        { id: 4, name: 'Palazzo Kurta Combo', price: '₹3,599', originalPrice: '₹4,999', badge: 'Trending', image: 'https://placehold.co/400x500/FFFBEB/1C1917?text=Palazzo' },
        { id: 5, name: 'Designer Lehenga Set', price: '₹15,999', originalPrice: '₹22,999', badge: 'Premium', image: 'https://placehold.co/400x500/FEF7ED/1C1917?text=Lehenga' },
        { id: 6, name: 'Cotton Printed Kurti', price: '₹1,299', originalPrice: '₹1,799', badge: 'Daily Wear', image: 'https://placehold.co/400x500/FEF3C7/1C1917?text=Kurti' }
    ];

    // Editorial spreads
    const editorials = [
        {
            title: 'The Art of Handloom',
            subtitle: 'Celebrating Indian Weaving Heritage',
            description: 'From Varanasi to Kanchipuram, discover the timeless artistry of Indian handlooms. Each piece tells a story of generations of skilled artisans.',
            image: 'https://placehold.co/1200x700/FEF7ED/1C1917?text=Handloom+Editorial',
            cta: 'Explore Collection'
        },
        {
            title: 'Modern Maharani',
            subtitle: 'Contemporary Bridal Couture',
            description: 'Redefining bridal elegance with modern silhouettes and traditional craftsmanship. For the bride who celebrates both heritage and individuality.',
            image: 'https://placehold.co/1200x700/FEF3C7/1C1917?text=Bridal+Editorial',
            cta: 'View Bridal'
        },
        {
            title: 'Festive Splendor',
            subtitle: 'Diwali Collection 2024',
            description: 'Illuminate your celebrations with our curated festive edit. Rich fabrics, intricate embroideries, and colors that capture the spirit of festivities.',
            image: 'https://placehold.co/1200x700/FDE68A/1C1917?text=Festive+Editorial',
            cta: 'Shop Festive'
        }
    ];

    // Testimonials - Indian customers
    const testimonials = [
        { text: 'The quality of the Banarasi saree exceeded my expectations. The zari work is exquisite and perfect for my daughter\'s wedding.', name: 'Priya Sharma', location: 'Mumbai', rating: 5 },
        { text: 'Finally found a brand that understands modern Indian aesthetics. Their kurta sets are perfect for both office and festivals.', name: 'Ananya Patel', location: 'Bangalore', rating: 5 },
        { text: 'The attention to detail in their embroidery work is remarkable. I\'ve received so many compliments on my lehenga!', name: 'Meera Reddy', location: 'Hyderabad', rating: 5 }
    ];

    // Instagram posts
    const instagramPosts = [
        { image: 'https://placehold.co/300x300/FEF7ED/1C1917?text=Insta+1', likes: '2.4k' },
        { image: 'https://placehold.co/300x300/FEF3C7/1C1917?text=Insta+2', likes: '1.8k' },
        { image: 'https://placehold.co/300x300/FFFBEB/1C1917?text=Insta+3', likes: '3.1k' },
        { image: 'https://placehold.co/300x300/FDE68A/1C1917?text=Insta+4', likes: '2.7k' },
        { image: 'https://placehold.co/300x300/FEF7ED/1C1917?text=Insta+5', likes: '1.9k' },
        { image: 'https://placehold.co/300x300/FEF3C7/1C1917?text=Insta+6', likes: '2.2k' }
    ];

    return (
        <div className="w-full min-h-screen bg-amber-50/30 font-sans">
            {/* Hero Section - Magazine Cover Style */}
            <section className="relative min-h-[90vh] lg:min-h-screen overflow-hidden bg-gradient-to-br from-amber-50 via-orange-50/30 to-amber-100/50">
                {/* Decorative Elements */}
                <div className="absolute top-20 left-10 w-32 h-32 bg-amber-200/30 rounded-full blur-3xl" />
                <div className="absolute bottom-40 right-20 w-48 h-48 bg-orange-200/30 rounded-full blur-3xl" />

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center min-h-[90vh] lg:min-h-screen py-16">
                        {/* Text Content */}
                        <div className="order-2 lg:order-1 text-center lg:text-left">
                            <span className="inline-block text-amber-700 text-sm font-semibold tracking-[0.2em] uppercase mb-4">
                                Editorial Collection
                            </span>
                            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-light text-stone-900 leading-[1.1] mb-6">
                                Weaving
                                <span className="block font-serif italic text-amber-700">Traditions</span>
                                Into Tomorrow
                            </h1>
                            <p className="text-stone-600 text-lg lg:text-xl max-w-lg mx-auto lg:mx-0 mb-8 leading-relaxed">
                                Discover the essence of Indian craftsmanship reimagined for the modern woman.
                                Where heritage meets contemporary elegance.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                                <button
                                    onClick={() => navigate('/template6/shop')}
                                    className="group px-8 py-4 bg-stone-900 text-white text-sm font-medium tracking-wider uppercase hover:bg-stone-800 transition-all duration-300 flex items-center justify-center gap-3"
                                >
                                    Explore Collection
                                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                </button>
                                <button className="px-8 py-4 border-2 border-stone-300 text-stone-700 text-sm font-medium tracking-wider uppercase hover:border-stone-900 hover:text-stone-900 transition-all duration-300">
                                    View Lookbook
                                </button>
                            </div>
                            {/* Stats */}
                            <div className="flex justify-center lg:justify-start gap-12 mt-12 pt-8 border-t border-stone-200">
                                <div>
                                    <p className="text-3xl font-light text-stone-900">500+</p>
                                    <p className="text-sm text-stone-500 mt-1">Artisan Partners</p>
                                </div>
                                <div>
                                    <p className="text-3xl font-light text-stone-900">50k+</p>
                                    <p className="text-sm text-stone-500 mt-1">Happy Customers</p>
                                </div>
                                <div>
                                    <p className="text-3xl font-light text-stone-900">25+</p>
                                    <p className="text-sm text-stone-500 mt-1">States Covered</p>
                                </div>
                            </div>
                        </div>

                        {/* Hero Image */}
                        <div className="order-1 lg:order-2 relative">
                            <div className="relative aspect-[3/4] max-w-md mx-auto lg:max-w-none">
                                {/* Main Image */}
                                <div className="relative z-10 overflow-hidden rounded-t-full">
                                    <img
                                        src="https://placehold.co/600x800/FEF7ED/1C1917?text=Hero+Model"
                                        alt="Featured Collection"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                {/* Decorative Frame */}
                                <div className="absolute -bottom-4 -right-4 w-full h-full border-2 border-amber-300 rounded-t-full -z-10" />
                                {/* Floating Badge */}
                                <div className="absolute -left-6 top-1/3 bg-white shadow-xl px-6 py-4 rounded-lg">
                                    <p className="text-xs text-stone-500 uppercase tracking-wider">Handcrafted</p>
                                    <p className="text-lg font-semibold text-stone-900">In India</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Brand Introduction */}
            <section className="py-20 lg:py-28 bg-white">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <span className="inline-block w-12 h-[2px] bg-amber-600 mb-8" />
                    <h2 className="text-3xl lg:text-4xl font-light text-stone-900 mb-6 leading-relaxed">
                        "We believe in fashion that honors the hands that create it and the culture it represents"
                    </h2>
                    <p className="text-stone-500 text-lg max-w-2xl mx-auto leading-relaxed">
                        Each piece in our collection is a celebration of Indian artistry,
                        crafted by skilled artisans using techniques passed down through generations.
                    </p>
                    <div className="mt-10 flex items-center justify-center gap-6">
                        <img src="https://placehold.co/60x60/FEF7ED/1C1917?text=GI" alt="GI Tag" className="h-12" />
                        <img src="https://placehold.co/60x60/FEF7ED/1C1917?text=HI" alt="Handloom India" className="h-12" />
                        <img src="https://placehold.co/60x60/FEF7ED/1C1917?text=MI" alt="Made in India" className="h-12" />
                    </div>
                </div>
            </section>

            {/* Categories Grid - Magazine Layout */}
            <section className="py-16 lg:py-24 bg-amber-50/50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12 lg:mb-16">
                        <span className="text-amber-700 text-sm font-semibold tracking-[0.2em] uppercase">Shop By Category</span>
                        <h2 className="text-3xl lg:text-4xl font-light text-stone-900 mt-3">Curated Collections</h2>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                        {categories.map((category, index) => (
                            <div
                                key={index}
                                onClick={() => navigate('/template6/shop')}
                                className="group cursor-pointer relative overflow-hidden bg-white rounded-lg shadow-sm hover:shadow-xl transition-all duration-500"
                            >
                                <div className="aspect-[3/4] overflow-hidden">
                                    <img
                                        src={category.image}
                                        alt={category.name}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    />
                                </div>
                                <div className="absolute inset-0 bg-gradient-to-t from-stone-900/60 via-transparent to-transparent" />
                                <div className="absolute bottom-0 left-0 right-0 p-4 lg:p-6">
                                    <h3 className="text-white text-lg lg:text-xl font-medium mb-1">{category.name}</h3>
                                    <p className="text-white/70 text-sm">{category.count}</p>
                                </div>
                                <div className="absolute top-4 right-4 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                                    <ArrowRight className="w-4 h-4 text-stone-900" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Editorial Spread Section */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12 lg:mb-16">
                        <span className="text-amber-700 text-sm font-semibold tracking-[0.2em] uppercase">From The Magazine</span>
                        <h2 className="text-3xl lg:text-4xl font-light text-stone-900 mt-3">Editorial Stories</h2>
                    </div>

                    {/* Main Editorial */}
                    <div className="relative overflow-hidden rounded-2xl bg-amber-50 mb-8">
                        <div className="grid lg:grid-cols-2">
                            <div className="aspect-video lg:aspect-auto lg:h-[500px] overflow-hidden">
                                <img
                                    src={editorials[activeEditorial].image}
                                    alt={editorials[activeEditorial].title}
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className="p-8 lg:p-12 flex flex-col justify-center">
                                <span className="text-amber-700 text-sm font-medium tracking-wider uppercase mb-4">
                                    {editorials[activeEditorial].subtitle}
                                </span>
                                <h3 className="text-3xl lg:text-4xl font-light text-stone-900 mb-6">
                                    {editorials[activeEditorial].title}
                                </h3>
                                <p className="text-stone-600 text-lg leading-relaxed mb-8">
                                    {editorials[activeEditorial].description}
                                </p>
                                <button
                                    onClick={() => navigate('/template6/shop')}
                                    className="self-start group flex items-center gap-3 text-stone-900 font-medium hover:text-amber-700 transition-colors"
                                >
                                    {editorials[activeEditorial].cta}
                                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Editorial Navigation */}
                    <div className="flex justify-center gap-4">
                        {editorials.map((_, index) => (
                            <button
                                key={index}
                                onClick={() => setActiveEditorial(index)}
                                className={`w-20 h-1 rounded-full transition-all duration-300 ${
                                    activeEditorial === index ? 'bg-amber-600' : 'bg-stone-200 hover:bg-stone-300'
                                }`}
                            />
                        ))}
                    </div>
                </div>
            </section>

            {/* Featured Products */}
            <section className="py-16 lg:py-24 bg-gradient-to-b from-amber-50/50 to-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-12 lg:mb-16">
                        <div>
                            <span className="text-amber-700 text-sm font-semibold tracking-[0.2em] uppercase">New Arrivals</span>
                            <h2 className="text-3xl lg:text-4xl font-light text-stone-900 mt-3">Editor's Picks</h2>
                        </div>
                        <button
                            onClick={() => navigate('/template6/shop')}
                            className="group flex items-center gap-2 text-stone-700 font-medium mt-4 lg:mt-0 hover:text-amber-700 transition-colors"
                        >
                            View All Products
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </button>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-8">
                        {products.map((product) => (
                            <div
                                key={product.id}
                                onClick={() => navigate('/template6/shop')}
                                className="group cursor-pointer"
                            >
                                <div className="relative aspect-[3/4] overflow-hidden bg-amber-50 rounded-lg mb-4">
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    />
                                    {/* Badge */}
                                    <span className="absolute top-3 left-3 px-3 py-1 bg-white/95 text-stone-800 text-xs font-medium rounded-full">
                                        {product.badge}
                                    </span>
                                    {/* Quick Actions */}
                                    <div className="absolute bottom-4 left-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                                        <button className="w-full py-3 bg-stone-900 text-white text-sm font-medium hover:bg-stone-800 transition-colors rounded">
                                            Quick View
                                        </button>
                                    </div>
                                    {/* Wishlist */}
                                    <button className="absolute top-3 right-3 w-9 h-9 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-white">
                                        <Heart className="w-4 h-4 text-stone-700" />
                                    </button>
                                </div>
                                <div className="space-y-2">
                                    <h3 className="text-stone-900 font-medium group-hover:text-amber-700 transition-colors line-clamp-1">
                                        {product.name}
                                    </h3>
                                    <div className="flex items-center gap-2">
                                        <span className="text-stone-900 font-semibold">{product.price}</span>
                                        <span className="text-stone-400 text-sm line-through">{product.originalPrice}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Craftsmanship Section */}
            <section className="py-16 lg:py-24 bg-stone-900 text-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                        <div>
                            <span className="text-amber-400 text-sm font-semibold tracking-[0.2em] uppercase">Our Promise</span>
                            <h2 className="text-3xl lg:text-4xl font-light text-white mt-3 mb-6">
                                Celebrating Indian Craftsmanship
                            </h2>
                            <p className="text-stone-300 text-lg leading-relaxed mb-8">
                                Every thread tells a story. From the silk weavers of Varanasi to the block printers of Jaipur,
                                we work directly with artisan communities across India, ensuring fair wages and preserving
                                traditional techniques for future generations.
                            </p>
                            <div className="grid grid-cols-2 gap-6 mb-8">
                                <div className="flex items-start gap-3">
                                    <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                                        <Shield className="w-5 h-5 text-amber-400" />
                                    </div>
                                    <div>
                                        <h4 className="text-white font-medium mb-1">Authentic Handloom</h4>
                                        <p className="text-stone-400 text-sm">GI tagged products</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                                        <Truck className="w-5 h-5 text-amber-400" />
                                    </div>
                                    <div>
                                        <h4 className="text-white font-medium mb-1">Free Shipping</h4>
                                        <p className="text-stone-400 text-sm">Pan-India delivery</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                                        <RefreshCw className="w-5 h-5 text-amber-400" />
                                    </div>
                                    <div>
                                        <h4 className="text-white font-medium mb-1">Easy Returns</h4>
                                        <p className="text-stone-400 text-sm">15-day return policy</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                                        <Clock className="w-5 h-5 text-amber-400" />
                                    </div>
                                    <div>
                                        <h4 className="text-white font-medium mb-1">COD Available</h4>
                                        <p className="text-stone-400 text-sm">Cash on delivery</p>
                                    </div>
                                </div>
                            </div>
                            <button
                                onClick={() => navigate('/template6/shop')}
                                className="group px-8 py-4 bg-amber-500 text-stone-900 text-sm font-semibold tracking-wider uppercase hover:bg-amber-400 transition-all duration-300 flex items-center gap-3"
                            >
                                Meet Our Artisans
                                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </button>
                        </div>
                        <div className="relative">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-4">
                                    <div className="aspect-[4/5] overflow-hidden rounded-lg">
                                        <img
                                            src="https://placehold.co/400x500/44403C/FFFFFF?text=Artisan+1"
                                            alt="Artisan at work"
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                    <div className="aspect-square overflow-hidden rounded-lg">
                                        <img
                                            src="https://placehold.co/300x300/57534E/FFFFFF?text=Craft"
                                            alt="Craftsmanship"
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                </div>
                                <div className="space-y-4 pt-8">
                                    <div className="aspect-square overflow-hidden rounded-lg">
                                        <img
                                            src="https://placehold.co/300x300/78716C/FFFFFF?text=Weaving"
                                            alt="Weaving"
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                    <div className="aspect-[4/5] overflow-hidden rounded-lg">
                                        <img
                                            src="https://placehold.co/400x500/44403C/FFFFFF?text=Artisan+2"
                                            alt="Artisan detail"
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                </div>
                            </div>
                            {/* Video Play Button Overlay */}
                            <button className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-xl hover:scale-110 transition-transform">
                                <Play className="w-8 h-8 text-stone-900 ml-1" />
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Testimonials */}
            <section className="py-16 lg:py-24 bg-amber-50/50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12 lg:mb-16">
                        <span className="text-amber-700 text-sm font-semibold tracking-[0.2em] uppercase">Customer Stories</span>
                        <h2 className="text-3xl lg:text-4xl font-light text-stone-900 mt-3">What They're Saying</h2>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
                        {testimonials.map((testimonial, index) => (
                            <div key={index} className="bg-white p-8 rounded-xl shadow-sm hover:shadow-lg transition-shadow duration-300">
                                <Quote className="w-10 h-10 text-amber-200 mb-4" />
                                <div className="flex gap-1 mb-4">
                                    {[...Array(testimonial.rating)].map((_, i) => (
                                        <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                                    ))}
                                </div>
                                <p className="text-stone-600 leading-relaxed mb-6">
                                    "{testimonial.text}"
                                </p>
                                <div className="border-t border-stone-100 pt-4">
                                    <p className="font-medium text-stone-900">{testimonial.name}</p>
                                    <p className="text-stone-500 text-sm">{testimonial.location}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Newsletter Section */}
            <section className="py-16 lg:py-24 bg-gradient-to-br from-amber-100 via-orange-50 to-amber-50">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <span className="text-amber-700 text-sm font-semibold tracking-[0.2em] uppercase">Stay Connected</span>
                    <h2 className="text-3xl lg:text-4xl font-light text-stone-900 mt-3 mb-4">
                        Join Our Community
                    </h2>
                    <p className="text-stone-600 text-lg mb-8">
                        Subscribe for exclusive previews, styling tips, and special offers.
                        Plus, get 15% off your first order!
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
                        <input
                            type="email"
                            placeholder="Enter your email"
                            className="flex-1 px-6 py-4 bg-white border border-stone-200 rounded-lg text-stone-900 placeholder-stone-400 focus:outline-none focus:border-amber-500 transition-colors"
                        />
                        <button className="px-8 py-4 bg-stone-900 text-white text-sm font-semibold tracking-wider uppercase hover:bg-stone-800 transition-colors rounded-lg whitespace-nowrap">
                            Subscribe
                        </button>
                    </div>
                    <p className="text-stone-500 text-sm mt-4">
                        No spam, unsubscribe anytime. Privacy policy applies.
                    </p>
                </div>
            </section>

            {/* Instagram Section */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <div className="flex items-center justify-center gap-3 mb-3">
                            <Instagram className="w-6 h-6 text-stone-900" />
                            <span className="text-stone-900 font-medium">@symmetryfashion</span>
                        </div>
                        <h2 className="text-3xl lg:text-4xl font-light text-stone-900">Follow Our Journey</h2>
                    </div>

                    <div className="grid grid-cols-3 md:grid-cols-6 gap-2 lg:gap-4">
                        {instagramPosts.map((post, index) => (
                            <div key={index} className="group relative aspect-square overflow-hidden rounded-lg cursor-pointer">
                                <img
                                    src={post.image}
                                    alt={`Instagram post ${index + 1}`}
                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                />
                                <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/40 transition-colors duration-300 flex items-center justify-center">
                                    <div className="flex items-center gap-2 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                                        <Heart className="w-5 h-5" />
                                        <span className="text-sm font-medium">{post.likes}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Services Bar */}
            <section className="py-12 bg-stone-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm">
                                <Truck className="w-5 h-5 text-amber-600" />
                            </div>
                            <div>
                                <p className="font-medium text-stone-900">Free Shipping</p>
                                <p className="text-stone-500 text-sm">On orders above ₹999</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm">
                                <RefreshCw className="w-5 h-5 text-amber-600" />
                            </div>
                            <div>
                                <p className="font-medium text-stone-900">Easy Returns</p>
                                <p className="text-stone-500 text-sm">15-day return policy</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm">
                                <Shield className="w-5 h-5 text-amber-600" />
                            </div>
                            <div>
                                <p className="font-medium text-stone-900">Secure Payment</p>
                                <p className="text-stone-500 text-sm">100% secure checkout</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm">
                                <Clock className="w-5 h-5 text-amber-600" />
                            </div>
                            <div>
                                <p className="font-medium text-stone-900">COD Available</p>
                                <p className="text-stone-500 text-sm">Pay on delivery</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Template6;
